import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../services/api';
import { WORKFLOW_STATUSES, VENDOR_WORKFLOW_STATUSES, INTERNAL_STATUSES, VENDOR_TYPES, SALE_DESTINATIONS } from './recon/constants';
import { QRCodePopup } from './VehicleQRCode';
import { VehicleFilesButton } from './VehicleFilesModal';
import VinDisplay from './shared/VinDisplay';

const STATUS_GROUPS = [
  { key: 'awaiting', label: 'Awaiting', statuses: ['awaiting_inspection', 'chris_to_double_check'] },
  { key: 'in_progress', label: 'In Progress', statuses: ['parts_ordered', 'repairs_in_progress'] },
  { key: 'vendor', label: 'Vendor / Mech', statuses: ['needs_pdr','needs_body_outside','needs_body_inhouse','needs_rim_wheel','needs_interior','needs_glass','needs_mechanical_outside','needs_inside_mech','need_new_vendor'] },
  { key: 'detail', label: 'Detail', statuses: ['at_detail'] },
  { key: 'ready', label: 'Ready', statuses: ['ready_for_sale'] },
];

const sColor = (s) => {
  if (s === 'ready_for_sale') return 'bg-green-100 text-green-800';
  if (s === 'at_detail') return 'bg-teal-100 text-teal-800';
  if (s === 'awaiting_inspection') return 'bg-orange-100 text-orange-800';
  if (s === 'parts_ordered') return 'bg-blue-100 text-blue-800';
  if (s === 'repairs_in_progress') return 'bg-yellow-100 text-yellow-800';
  if (s.includes('needs_')) return 'bg-purple-100 text-purple-800';
  return 'bg-neutral-100 text-neutral-800';
};
const eLabel = (e) => ({retail:'Retail',wholesale:'Wholesale',dealer_direct:'Byrider/DD'}[e]||e||'Retail');
const eColor = (e) => ({retail:'bg-green-100 text-green-700',wholesale:'bg-blue-100 text-blue-700',dealer_direct:'bg-red-100 text-red-700'}[e]||'bg-neutral-100 text-neutral-600');
const sLabel = (s) => WORKFLOW_STATUSES.find(w=>w.value===s)?.label||s;

const StatusDropdown = ({ vehicle, onUpdate, saving }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const sts = vehicle.reconWorkflowStatuses || [vehicle.reconWorkflowStatus || 'awaiting_inspection'];
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const toggle = (val) => {
    let next;
    if (sts.includes(val)) { next = sts.filter(s => s !== val); if (next.length === 0) next = ['awaiting_inspection']; }
    else { next = [...sts, val]; }
    onUpdate(vehicle.id, 'reconWorkflowStatuses', next);
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={(e)=>{e.stopPropagation();setOpen(!open)}} disabled={saving}
        className="text-xs px-2 py-1 rounded border border-neutral-300 bg-white hover:bg-neutral-50 w-full text-left flex items-center justify-between gap-1">
        <span className="truncate">{sts.length === 1 ? sLabel(sts[0]) : sts.length + ' statuses'}</span>
        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 bg-white border rounded-lg shadow-lg py-1 w-56 max-h-64 overflow-y-auto left-0" onClick={e=>e.stopPropagation()}>
          {WORKFLOW_STATUSES.map(st => (
            <label key={st.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-neutral-50 cursor-pointer text-xs">
              <input type="checkbox" checked={sts.includes(st.value)} onChange={()=>toggle(st.value)} className="rounded text-blue-600"/>
              <span>{st.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const Card = ({ vehicle, thumb, onClick, onRO, onCreateRO, creatingRO, onUpdate, saving, dh, runList, onAddToList, todoCount, onTodo }) => {
  const sts = vehicle.reconWorkflowStatuses || [vehicle.reconWorkflowStatus || 'awaiting_inspection'];
  return (
    <div className="border-2 border-neutral-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all bg-white" draggable="true" {...dh}>
      <div className="flex cursor-pointer" onClick={()=>onClick(vehicle)}>
        {thumb ? <img src={thumb} alt="" className="w-24 h-24 object-cover rounded-tl-lg flex-shrink-0" onError={e=>{e.target.style.display='none'}}/> : <div className="w-24 h-24 bg-neutral-200 rounded-tl-lg flex items-center justify-center text-neutral-400 text-xs flex-shrink-0">No Photo</div>}
        <div className="flex-1 p-2 min-w-0">
          <div className="flex justify-between items-start gap-1"><span className="font-mono text-sm font-bold text-blue-700 truncate">{vehicle.stockNumber}</span><span className={"text-xs px-2 py-0.5 rounded-full flex-shrink-0 " + eColor(vehicle.endUse)}>{eLabel(vehicle.endUse)}</span></div>
          <div className="font-bold text-sm text-neutral-800 truncate">{vehicle.year} {vehicle.make} {vehicle.model}</div>
          <div className="text-xs text-neutral-500">{vehicle.color}{vehicle.mileage ? ' - ' + Number(vehicle.mileage).toLocaleString() + ' mi' : ''}</div>
          {vehicle.vin && <VinDisplay vin={vehicle.vin} className="mt-0.5" />}
        </div>
      </div>
      <div className="px-3 py-1.5"><StatusDropdown vehicle={vehicle} onUpdate={onUpdate} saving={saving}/></div>
      <div className="px-3 py-1 flex flex-wrap gap-1">{sts.map(s=><span key={s} className={"text-xs px-2 py-0.5 rounded-full " + sColor(s)}>{sLabel(s)}</span>)}</div>
      <div className="px-3 pb-2 cursor-pointer" onClick={()=>onClick(vehicle)}>
        <div className="bg-neutral-50 rounded-lg p-2 text-xs italic text-neutral-600 hover:bg-neutral-100">
          {vehicle.reconGamePlan ? (vehicle.reconGamePlan.length > 60 ? vehicle.reconGamePlan.substring(0,60)+'...' : vehicle.reconGamePlan) : <span className="text-neutral-400">Click to add notes</span>}
        </div>
      </div>
      {vehicle.currentLocation && <div className="px-3 pb-2 text-xs text-neutral-500">LOC: {vehicle.currentLocation}</div>}
      {/* Parts status */}
      {(vehicle.partsOrdered > 0 || vehicle.partsArrived > 0) && (
        <div className="px-3 pb-1 flex items-center gap-1 text-xs">
          <span>{vehicle.partsOrdered > 0 ? '🟡' : '🟢'}</span>
          <span className="text-neutral-600">
            {vehicle.partsOrdered > 0 && `${vehicle.partsOrdered} part${vehicle.partsOrdered>1?'s':''} pending`}
            {vehicle.partsOrdered > 0 && vehicle.partsArrived > 0 && ' / '}
            {vehicle.partsArrived > 0 && `${vehicle.partsArrived} here`}
          </span>
        </div>
      )}
      <div className="px-3 py-2 bg-neutral-50 border-t flex items-center justify-between gap-1 flex-wrap">
        <div className="flex gap-1 items-center flex-wrap">
          {vehicle.hasOpenRO || vehicle.roId ? (
            <button onClick={e=>{e.stopPropagation();onRO(vehicle.roId)}} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">View RO</button>
          ) : (
            <button onClick={e=>{e.stopPropagation();onCreateRO(vehicle)}} disabled={creatingRO===vehicle.id}
              className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50">
              {creatingRO===vehicle.id ? '...' : '+ RO'}
            </button>
          )}
          <VehicleFilesButton vehicleId={vehicle.id} vehicle={vehicle} className="text-xs"/>
          <QRCodePopup vehicle={vehicle} />
          <button onClick={e=>{e.stopPropagation();onTodo(vehicle)}} className="relative text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded hover:bg-neutral-200" title="Admin tasks">
            📋{todoCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{todoCount}</span>}
          </button>
        </div>
        <div className="flex gap-1 items-center">
          {runList.some(v => v.id === vehicle.id) ? (
            <span className="text-xs text-green-600 font-medium">✓ Listed</span>
          ) : (
            <button onClick={e=>{e.stopPropagation();onAddToList(vehicle)}} className="text-xs px-2 py-1 bg-neutral-800 text-white rounded hover:bg-neutral-700">+ List</button>
          )}
          <button onClick={(e)=>{e.stopPropagation();onClick(vehicle)}} className="text-xs px-2 py-1 bg-neutral-200 text-neutral-700 rounded hover:bg-neutral-300">Details</button>
        </div>
      </div>
    </div>
  );
};

const DetailPanel = ({ vehicle, thumbUrl, onClose, onUpdate, onRO, onCreateRO, creatingRO, saving }) => {
  const [notes, setNotes] = useState(vehicle.reconGamePlan || '');
  const [location, setLocation] = useState(vehicle.currentLocation || '');
  const [endUse, setEndUse] = useState(vehicle.endUse || 'retail');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const sts = vehicle.reconWorkflowStatuses || [vehicle.reconWorkflowStatus || 'awaiting_inspection'];

  useEffect(() => {
    setNotes(vehicle.reconGamePlan || '');
    setLocation(vehicle.currentLocation || '');
    setEndUse(vehicle.endUse || 'retail');
  }, [vehicle]);

  const toggleStatus = async (val) => {
    let next;
    if (sts.includes(val)) { next = sts.filter(s => s !== val); if (next.length === 0) next = ['awaiting_inspection']; }
    else { next = [...sts, val]; }
    await onUpdate(vehicle.id, 'reconWorkflowStatuses', next);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const r = await api.uploadFile(file, 'vehicles');
        await api.addVehicleFile({ vehicleId: vehicle.id, fileUrl: r.url, fileType: file.type.startsWith('image') ? 'image' : 'document', category: 'upload', originalName: file.name });
      }
      alert('Photos uploaded!');
    } catch (err) { alert('Upload failed: ' + err.message); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b bg-neutral-50 rounded-t-xl flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-bold text-blue-700">{vehicle.stockNumber}</span>
              <span className={"text-xs px-2 py-0.5 rounded-full " + eColor(vehicle.endUse)}>{eLabel(vehicle.endUse)}</span>
            </div>
            <div className="font-bold text-neutral-800">{vehicle.year} {vehicle.make} {vehicle.model}</div>
            <div className="text-xs text-neutral-500">{vehicle.color} {vehicle.vin ? '| VIN: ...' + vehicle.vin.slice(-8) : ''}{vehicle.mileage ? ' | ' + Number(vehicle.mileage).toLocaleString() + ' mi' : ''}</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl leading-none">&times;</button>
        </div>
        {/* Photo + Upload + RO */}
        <div className="p-4 border-b flex gap-4">
          <div className="flex-shrink-0">
            {thumbUrl ? <img src={thumbUrl} alt="" className="w-32 h-32 object-cover rounded-lg"/> : <div className="w-32 h-32 bg-neutral-200 rounded-lg flex items-center justify-center text-neutral-400 text-sm">No Photo</div>}
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex gap-2 flex-wrap">
              <button onClick={()=>fileRef.current?.click()} disabled={uploading} className="px-3 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50">
                {uploading ? 'Uploading...' : 'Upload Photos'}
              </button>
              <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={handleFileSelect}/>
              <VehicleFilesButton vehicleId={vehicle.id} vehicle={vehicle} className="text-xs"/>
              <QRCodePopup vehicle={vehicle} />
            </div>
            <div className="flex gap-2 flex-wrap">
              {vehicle.hasOpenRO || vehicle.roId ? (
                <button onClick={()=>onRO(vehicle.roId)} className="px-3 py-2 bg-blue-100 text-blue-700 text-xs rounded-lg hover:bg-blue-200">View RO #{vehicle.roId}</button>
              ) : (
                <button onClick={()=>onCreateRO(vehicle)} disabled={creatingRO===vehicle.id}
                  className="px-3 py-2 bg-green-100 text-green-700 text-xs rounded-lg hover:bg-green-200 disabled:opacity-50">
                  {creatingRO===vehicle.id ? 'Creating...' : '+ Create RO'}
                </button>
              )}
            </div>
            {/* Parts status */}
            {(vehicle.partsOrdered > 0 || vehicle.partsArrived > 0) && (
              <div className="flex items-center gap-1 text-xs">
                <span>{vehicle.partsOrdered > 0 ? '🟡' : '🟢'}</span>
                <span className="text-neutral-600">
                  {vehicle.partsOrdered > 0 && `${vehicle.partsOrdered} part${vehicle.partsOrdered>1?'s':''} pending`}
                  {vehicle.partsOrdered > 0 && vehicle.partsArrived > 0 && ' / '}
                  {vehicle.partsArrived > 0 && `${vehicle.partsArrived} arrived`}
                </span>
              </div>
            )}
          </div>
        </div>
        {/* Statuses */}
        <div className="p-4 border-b">
          <h4 className="text-sm font-semibold mb-2">Workflow Statuses</h4>
          <div className="text-xs text-neutral-500 mb-1 font-medium">Internal</div>
          <div className="flex flex-wrap gap-1 mb-2">
            {INTERNAL_STATUSES.map(st => (
              <button key={st.value} onClick={()=>toggleStatus(st.value)}
                className={"text-xs px-2.5 py-1 rounded-full border transition-colors " + (sts.includes(st.value) ? sColor(st.value) + ' border-current font-bold' : 'bg-white text-neutral-500 border-neutral-200 hover:bg-neutral-50')}>
                {st.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-neutral-500 mb-1 font-medium">Vendor Needs</div>
          <div className="flex flex-wrap gap-1">
            {VENDOR_WORKFLOW_STATUSES.map(st => (
              <button key={st.value} onClick={()=>toggleStatus(st.value)}
                className={"text-xs px-2.5 py-1 rounded-full border transition-colors " + (sts.includes(st.value) ? 'bg-purple-100 text-purple-800 border-purple-300 font-bold' : 'bg-white text-neutral-500 border-neutral-200 hover:bg-neutral-50')}>
                {st.label}
              </button>
            ))}
          </div>
        </div>
        {/* Location + End Use */}
        <div className="p-4 border-b grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-neutral-600 mb-1 block">Location</label>
            <input type="text" value={location} onChange={e=>setLocation(e.target.value)}
              onBlur={()=>onUpdate(vehicle.id,'currentLocation',location)}
              className="w-full px-2 py-1.5 border rounded-lg text-sm" placeholder="e.g. Main Lot"/>
          </div>
          <div>
            <label className="text-xs font-medium text-neutral-600 mb-1 block">Exit Strategy</label>
            <select value={endUse} onChange={e=>{setEndUse(e.target.value);onUpdate(vehicle.id,'endUse',e.target.value);}}
              className="w-full px-2 py-1.5 border rounded-lg text-sm">
              <option value="retail">Retail</option>
              <option value="wholesale">Wholesale</option>
              <option value="dealer_direct">Byrider / Dealer Direct</option>
            </select>
          </div>
        </div>
        {/* Notes */}
        <div className="p-4 border-b">
          <label className="text-xs font-medium text-neutral-600 mb-1 block">Game Plan / Notes</label>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)} className="w-full h-32 px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          <div className="flex justify-end mt-2">
            <button onClick={()=>onUpdate(vehicle.id,'reconGamePlan',notes)} disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </div>
        {/* Quick Links */}
        <div className="p-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral-500 font-medium">Parts:</span>
          <a href="https://www.car-part.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-orange-500 text-white rounded text-xs font-bold hover:bg-orange-600">Car-Part</a>
          <a href="https://www.ebay.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-yellow-400 text-black rounded text-xs font-bold hover:bg-yellow-300">eBay</a>
          <a href="https://www.amazon.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700">Amazon</a>
        </div>
      </div>
    </div>
  );
};

const ShopWorkflow = ({ onRefresh, onSilentRefresh, onSelectVehicle, currentUser, onNavigateToRO }) => {
  const [vehicles, setVehicles] = useState([]);
  const [vendorNeeds, setVendorNeeds] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeGrp, setActiveGrp] = useState('all');
  const [saving, setSaving] = useState(null);
  const [detailVehicle, setDetailVehicle] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [creatingRO, setCreatingRO] = useState(null);

  // Filters — full show-only/hide per status
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showOnlyStatuses, setShowOnlyStatuses] = useState([]);
  const [hiddenStatuses, setHiddenStatuses] = useState(() => {
    try { return JSON.parse(localStorage.getItem('shopwf_hidden') || '[]'); } catch { return []; }
  });
  const [filterEndUse, setFilterEndUse] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');

  // Run list
  const [runList, setRunList] = useState(() => {
    try { return JSON.parse(localStorage.getItem('workflow_runlist') || '[]'); } catch { return []; }
  });
  const [showRunList, setShowRunList] = useState(false);
  const [addToListVehicle, setAddToListVehicle] = useState(null);

  // QR popup
  const [showQRPopup, setShowQRPopup] = useState(false);

  // Vendor punch list
  const [selectedVendorType, setSelectedVendorType] = useState(null);
  const [punchList, setPunchList] = useState([]);
  const [loadingPunchList, setLoadingPunchList] = useState(false);
  const [costPrompt, setCostPrompt] = useState(null);

  // Admin todos
  const [adminTodos, setAdminTodos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('workflow_admin_todos') || '[]'); } catch { return []; }
  });
  const [todoVehicle, setTodoVehicle] = useState(null);

  const synced = useRef(false);

  // Persist
  useEffect(() => { localStorage.setItem('shopwf_hidden', JSON.stringify(hiddenStatuses)); }, [hiddenStatuses]);
  useEffect(() => { localStorage.setItem('workflow_runlist', JSON.stringify(runList)); }, [runList]);
  useEffect(() => { localStorage.setItem('workflow_admin_todos', JSON.stringify(adminTodos)); }, [adminTodos]);

  // Admin todo helpers
  const addTodo = (vehicleId, text) => { if (!text.trim()) return; setAdminTodos(prev => [...prev, { id: Date.now(), vehicleId, text: text.trim(), done: false, createdAt: new Date().toISOString() }]); };
  const toggleTodo = (todoId) => { setAdminTodos(prev => prev.map(t => t.id === todoId ? { ...t, done: !t.done } : t)); };
  const deleteTodo = (todoId) => { setAdminTodos(prev => prev.filter(t => t.id !== todoId)); };
  const getTodoCount = (vehicleId) => adminTodos.filter(t => t.vehicleId === vehicleId && !t.done).length;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (!synced.current) { await api.syncVendorStatuses().catch(()=>{}); synced.current = true; }
      const [vd, td, vn] = await Promise.all([
        api.getVehicles({includeSold:false}),
        api.getVehicleThumbnails().catch(()=>({})),
        api.getVendorNeeds().catch(()=>[])
      ]);
      setVehicles(vd.filter(v=>!['shop_personal','copart','iaai','salvage'].includes(v.endUse)));
      setThumbs(td);
      setVendorNeeds(vn);
    } catch(e) { setVehicles([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load punch list
  const loadPunchList = useCallback(async (vendorType) => {
    setLoadingPunchList(true);
    try { const data = await api.getVendorPunchList(vendorType); setPunchList(data || []); }
    catch { setPunchList([]); }
    finally { setLoadingPunchList(false); }
  }, []);
  useEffect(() => { if (selectedVendorType) loadPunchList(selectedVendorType); }, [selectedVendorType, loadPunchList]);

  const upd = useCallback(async (id, f, v) => {
    setSaving(id + '-' + f);
    try {
      let updateData = { [f]: v };
      if (f === 'reconWorkflowStatuses' && Array.isArray(v)) {
        const deduped = [...new Set(v)];
        updateData.reconWorkflowStatuses = deduped;
        updateData.reconWorkflowStatus = deduped[0] || 'awaiting_inspection';
      }
      if (f === 'endUse') await api.updateVehicle(id, { endUse: v });
      else if (f==='reconWorkflowStatuses'||f==='reconGamePlan'||f==='currentLocation') await api.updateVehicleWorkflow(id, updateData);
      else await api.updateVehicle(id,{[f]:v});
      setVehicles(p=>p.map(x=>x.id===id?{...x,...updateData}:x));
      setDetailVehicle(prev => prev && prev.id === id ? {...prev,...updateData} : prev);
    } catch(e) { alert('Failed: '+e.message); } finally { setSaving(null); }
  }, []);

  // Create RO
  const handleCreateRO = async (vehicle) => {
    setCreatingRO(vehicle.id);
    try {
      const newRO = await api.createRepairOrder({ vehicleId: vehicle.id });
      if (onRefresh) onRefresh();
      if (newRO?.id) {
        localStorage.setItem('pendingROId', newRO.id.toString());
        window.open(window.location.origin + window.location.pathname + '#recon', '_blank');
      }
      const vehicleData = await api.getVehicles({ includeSold: false });
      setVehicles(vehicleData.filter(v=>!['shop_personal','copart','iaai','salvage'].includes(v.endUse)));
    } catch (error) {
      alert('Failed to create RO: ' + error.message);
    } finally { setCreatingRO(null); }
  };

  // Run list helpers
  const addToRunList = (vehicle, destination) => {
    const deliveredCost = parseFloat(vehicle.purchasePrice || 0) + parseFloat(vehicle.auctionFees || 0) + parseFloat(vehicle.transportCost || 0);
    const reconCost = parseFloat(vehicle.reconCost || 0);
    const tiv = deliveredCost + reconCost;
    const askingPrice = parseFloat(vehicle.askingPrice || vehicle.retailPrice || 0);
    let statuses = vehicle.reconWorkflowStatuses;
    if (!statuses || statuses.length === 0) statuses = vehicle.reconWorkflowStatus ? [vehicle.reconWorkflowStatus] : ['awaiting_inspection'];
    const entry = { id: vehicle.id, stockNumber: vehicle.stockNumber, year: vehicle.year, make: vehicle.make, model: vehicle.model, destination, needs: statuses, notes: vehicle.reconGamePlan || '', location: vehicle.currentLocation || 'On Lot', addedAt: new Date().toISOString(), deliveredCost, reconCost, tiv, askingPrice, potentialProfit: askingPrice - tiv };
    setRunList(prev => { if (prev.some(v => v.id === vehicle.id)) return prev; return [...prev, entry]; });
    setAddToListVehicle(null);
  };
  const removeFromRunList = (vehicleId) => { setRunList(prev => prev.filter(v => v.id !== vehicleId)); };
  const updateRunListField = (vehicleId, field, value) => { setRunList(prev => prev.map(v => v.id === vehicleId ? { ...v, [field]: value } : v)); };
  const clearRunList = () => { if (window.confirm('Clear the entire run list?')) setRunList([]); };

  // Mark vendor complete
  const markVendorComplete = async (vehicle, vendorType, actualCost) => {
    const vendorToStatusMap = { 'pdr':'needs_pdr','body_outside':'needs_body_outside','body_shop_outside':'needs_body_outside','body_inhouse':'needs_body_inhouse','body_shop_inhouse':'needs_body_inhouse','rim_wheel':'needs_rim_wheel','interior':'needs_interior','interior_repair':'needs_interior','glass':'needs_glass','mechanical_outside':'needs_mechanical_outside','detail':'at_detail','other':'needs_inside_mech','inside_mech':'needs_inside_mech' };
    const statusToRemove = vendorToStatusMap[vendorType];
    if (statusToRemove && vehicle.id) {
      const currentStatuses = vehicle.reconWorkflowStatuses || [vehicle.reconWorkflowStatus || 'awaiting_inspection'];
      const newStatuses = currentStatuses.filter(s => s !== statusToRemove);
      const finalStatuses = newStatuses.length > 0 ? newStatuses : ['awaiting_inspection'];
      await api.updateVehicleWorkflow(vehicle.id, { reconWorkflowStatuses: finalStatuses, reconWorkflowStatus: finalStatuses[0] });
    }
    if (vehicle.roId) { try { await api.markVendorComplete(vehicle.roId, vendorType, parseFloat(actualCost) || 0); } catch {} }
  };

  const handleCostSubmit = async (actualCost, dontKnow) => {
    if (!costPrompt) return;
    const { vehicle, vendorType } = costPrompt;
    try {
      await markVendorComplete(vehicle, vendorType, actualCost);
      if (selectedVendorType) loadPunchList(selectedVendorType);
      setCostPrompt(null);
      if (onRefresh) onRefresh();
    } catch (error) { alert('Failed: ' + error.message); }
  };

  // CSV download
  const downloadTaskReport = () => {
    const headers = ['Stock #','Year','Make','Model','VIN','Status','Exit Strategy','Location','Game Plan'];
    const rows = filt.map(v => {
      const statuses = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
      const statusLabels = statuses.map(s => WORKFLOW_STATUSES.find(ws => ws.value === s)?.label || s).join(' / ');
      return [v.stockNumber,v.year,v.make,v.model,v.vin,statusLabels,v.endUse||'retail',v.currentLocation||'',(v.reconGamePlan||'').replace(/,/g,';')];
    });
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `workflow_task_report_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const onDragStart = useCallback((e,id)=>{e.dataTransfer.effectAllowed='move';e.dataTransfer.setData('text/plain',id.toString());},[]);
  const onDragOver = useCallback((e,k)=>{e.preventDefault();setDragOver(k);},[]);
  const onDrop = useCallback(async(e,k)=>{e.preventDefault();setDragOver(null);const id=parseInt(e.dataTransfer.getData('text/plain'));const g=STATUS_GROUPS.find(x=>x.key===k);if(!id||!g)return;await upd(id,'reconWorkflowStatuses',[g.statuses[0]]);},[upd]);

  const allLocations = [...new Set(vehicles.map(v=>v.currentLocation).filter(Boolean))];

  // Status counts
  const statusCounts = {};
  WORKFLOW_STATUSES.forEach(s => { statusCounts[s.value] = 0; });
  vehicles.forEach(v => {
    const sts = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
    sts.forEach(s => { statusCounts[s] = (statusCounts[s] || 0) + 1; });
  });

  const filt = vehicles.filter(v => {
    if (search) { const s=search.toLowerCase(); if (!((v.stockNumber||'').toLowerCase().includes(s)||(v.make||'').toLowerCase().includes(s)||(v.model||'').toLowerCase().includes(s)||(v.year?.toString()||'').includes(s)||(v.vin||'').toLowerCase().includes(s)||(v.reconGamePlan||'').toLowerCase().includes(s))) return false; }
    if (activeGrp!=='all') { const g=STATUS_GROUPS.find(x=>x.key===activeGrp); if(g){const st=v.reconWorkflowStatuses||[v.reconWorkflowStatus||'awaiting_inspection'];if(!st.some(s=>g.statuses.includes(s)))return false;} }
    // Hidden statuses
    const vSts = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
    if (vSts.every(s => hiddenStatuses.includes(s))) return false;
    // Show only
    if (showOnlyStatuses.length > 0 && !vSts.some(s => showOnlyStatuses.includes(s))) return false;
    if (filterEndUse!=='all' && v.endUse !== filterEndUse) return false;
    if (filterLocation!=='all' && (v.currentLocation||'') !== filterLocation) return false;
    return true;
  });

  const grpV = (k) => { const g=STATUS_GROUPS.find(x=>x.key===k); if(!g)return[]; return filt.filter(v=>{const st=v.reconWorkflowStatuses||[v.reconWorkflowStatus||'awaiting_inspection'];return st.some(s=>g.statuses.includes(s));}); };
  const counts = STATUS_GROUPS.reduce((a,g)=>{a[g.key]=vehicles.filter(v=>{const st=v.reconWorkflowStatuses||[v.reconWorkflowStatus||'awaiting_inspection'];return st.some(s=>g.statuses.includes(s));}).length;return a;},{});

  if (loading) return <div className="flex items-center justify-center py-20 text-neutral-500">Loading Recon Workflow...</div>;

  const renderCards = (list) => list.map(v => (
    <Card key={v.id} vehicle={v} thumb={thumbs[v.id]}
      onClick={v2=>setDetailVehicle(v2)} onRO={onNavigateToRO} onCreateRO={handleCreateRO} creatingRO={creatingRO}
      onUpdate={upd} saving={saving}
      dh={{onDragStart:e=>onDragStart(e,v.id),onDragEnd:()=>setDragOver(null)}}
      runList={runList} onAddToList={v2=>setAddToListVehicle(v2)}
      todoCount={getTodoCount(v.id)} onTodo={v2=>setTodoVehicle(v2)}
    />
  ));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-neutral-800 text-white px-6 py-4 rounded-xl flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold">Recon Workflow</h1>
          <span className="bg-blue-500 text-xs px-2 py-0.5 rounded-full font-bold">{vehicles.length}</span>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* QR */}
          <div className="relative">
            <button onClick={() => setShowQRPopup(!showQRPopup)} className="px-2 py-1.5 bg-neutral-700 text-white rounded-lg text-xs hover:bg-neutral-600" title="QR Code for Run List">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
            </button>
            {showQRPopup && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowQRPopup(false)} />
                <div className="absolute right-0 mt-2 z-50 bg-white rounded-lg shadow-xl border p-4" style={{minWidth:'200px'}}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-medium text-neutral-500">Scan for Run List</span>
                    <button onClick={() => setShowQRPopup(false)} className="text-neutral-400 hover:text-neutral-600">✕</button>
                  </div>
                  <div className="flex justify-center"><QRCodeSVG value="https://lotsense.thewholesalecars.com/runlist" size={150} level="M" /></div>
                  <div className="text-center mt-2 text-xs text-neutral-500">Weekly Run List</div>
                </div>
              </>
            )}
          </div>
          <a href="https://www.car-part.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-bold hover:bg-orange-600">Car-Part</a>
          <a href="https://www.ebay.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1.5 bg-yellow-400 text-black rounded-lg text-xs font-bold hover:bg-yellow-300">eBay</a>
          <a href="https://www.amazon.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700">Amazon</a>
          <button onClick={() => setShowRunList(true)} className="px-3 py-1.5 bg-neutral-700 text-white rounded-lg text-xs hover:bg-neutral-600 flex items-center gap-1">
            📋 Run List {runList.length > 0 && <span className="bg-white text-neutral-800 px-1.5 py-0.5 rounded text-xs font-bold">{runList.length}</span>}
          </button>
          <button onClick={downloadTaskReport} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700">📥 CSV</button>
          <button onClick={loadData} className="px-2 py-1.5 bg-neutral-700 text-white rounded-lg text-xs hover:bg-neutral-600">Refresh</button>
        </div>
      </div>

      {/* Vendor Needs Board */}
      {vendorNeeds.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><span className="text-lg">🔧</span> Work Type Summary</h3>
          <p className="text-xs text-neutral-500 mb-3">Click to see vehicles and print punch list</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {vendorNeeds.map(vn => (
              <button key={vn.vendor_type} onClick={() => setSelectedVendorType(vn.vendor_type)}
                className="bg-white border border-neutral-200 rounded-lg p-3 text-left hover:bg-blue-50 transition shadow-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-neutral-800 text-sm truncate pr-2">{VENDOR_TYPES.find(v => v.value === vn.vendor_type)?.label || vn.vendor_type}</span>
                  <span className="bg-neutral-800 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">{vn.count}</span>
                </div>
                <div className="text-xs text-neutral-500">{vn.count} vehicle{vn.count !== 1 ? 's' : ''}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Group Tabs + Search + Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={()=>setActiveGrp('all')} className={"px-3 py-1.5 rounded-full text-xs font-medium " + (activeGrp==='all'?'bg-neutral-800 text-white':'bg-white border text-neutral-600 hover:bg-neutral-50')}>All ({vehicles.length})</button>
        {STATUS_GROUPS.map(g=><button key={g.key} onClick={()=>setActiveGrp(g.key)} className={"px-3 py-1.5 rounded-full text-xs font-medium " + (activeGrp===g.key?'bg-neutral-800 text-white':'bg-white border text-neutral-600 hover:bg-neutral-50')}>{g.label} ({counts[g.key]||0})</button>)}
        {(showOnlyStatuses.length > 0 || hiddenStatuses.length > 0) && (
          <button onClick={() => { setShowOnlyStatuses([]); setHiddenStatuses([]); }}
            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded flex items-center gap-1 hover:bg-red-200">
            Clear Filters ({showOnlyStatuses.length + hiddenStatuses.length}) ×
          </button>
        )}
        <button onClick={()=>setShowFilterPanel(!showFilterPanel)} className={"px-3 py-1.5 rounded-full text-xs font-medium border " + (showFilterPanel ? 'bg-blue-600 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50')}>
          {showFilterPanel ? '▼' : '▶'} Filters
        </button>
        <input type="text" placeholder="Search stock#, make, model, VIN, notes..." value={search} onChange={e=>setSearch(e.target.value)} className="ml-auto px-3 py-1.5 border rounded-lg text-xs w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        <span className="text-xs text-neutral-500">{filt.length} vehicles</span>
      </div>

      {/* Full Filter Panel — Show Only / Hide per status */}
      {showFilterPanel && (
        <div className="bg-neutral-50 rounded-lg p-4 space-y-4">
          {/* Internal Statuses */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-neutral-700">Internal Statuses</span>
              <div className="flex gap-2">
                <button onClick={() => setShowOnlyStatuses(prev => [...new Set([...prev, ...INTERNAL_STATUSES.map(s => s.value)])])} className="text-xs text-green-600 hover:underline">Show All</button>
                <button onClick={() => setHiddenStatuses(prev => [...new Set([...prev, ...INTERNAL_STATUSES.map(s => s.value)])])} className="text-xs text-red-600 hover:underline">Hide All</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {INTERNAL_STATUSES.map(s => {
                const isShowOnly = showOnlyStatuses.includes(s.value);
                const isHidden = hiddenStatuses.includes(s.value);
                return (
                  <button key={s.value}
                    onClick={() => {
                      if (isHidden) setHiddenStatuses(prev => prev.filter(v => v !== s.value));
                      else if (isShowOnly) setShowOnlyStatuses(prev => prev.filter(v => v !== s.value));
                      else setShowOnlyStatuses(prev => [...prev, s.value]);
                    }}
                    onContextMenu={(e) => { e.preventDefault(); if (isHidden) setHiddenStatuses(prev => prev.filter(v => v !== s.value)); else { setHiddenStatuses(prev => [...prev, s.value]); setShowOnlyStatuses(prev => prev.filter(v => v !== s.value)); } }}
                    className={`text-xs px-3 py-1.5 rounded-full transition ${isShowOnly ? 'bg-green-600 text-white' : isHidden ? 'bg-red-600 text-white line-through' : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-100'}`}
                    title="Click to show only, right-click to hide"
                  >
                    {s.label} <span className="ml-1 opacity-70">({statusCounts[s.value]||0})</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Vendor Statuses */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-neutral-700">Vendor Statuses</span>
              <div className="flex gap-2">
                <button onClick={() => setShowOnlyStatuses(prev => [...new Set([...prev, ...VENDOR_WORKFLOW_STATUSES.map(s => s.value)])])} className="text-xs text-green-600 hover:underline">Show All</button>
                <button onClick={() => setHiddenStatuses(prev => [...new Set([...prev, ...VENDOR_WORKFLOW_STATUSES.map(s => s.value)])])} className="text-xs text-red-600 hover:underline">Hide All</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {VENDOR_WORKFLOW_STATUSES.map(s => {
                const isShowOnly = showOnlyStatuses.includes(s.value);
                const isHidden = hiddenStatuses.includes(s.value);
                return (
                  <button key={s.value}
                    onClick={() => {
                      if (isHidden) setHiddenStatuses(prev => prev.filter(v => v !== s.value));
                      else if (isShowOnly) setShowOnlyStatuses(prev => prev.filter(v => v !== s.value));
                      else setShowOnlyStatuses(prev => [...prev, s.value]);
                    }}
                    onContextMenu={(e) => { e.preventDefault(); if (isHidden) setHiddenStatuses(prev => prev.filter(v => v !== s.value)); else { setHiddenStatuses(prev => [...prev, s.value]); setShowOnlyStatuses(prev => prev.filter(v => v !== s.value)); } }}
                    className={`text-xs px-3 py-1.5 rounded-full transition ${isShowOnly ? 'bg-green-600 text-white' : isHidden ? 'bg-red-600 text-white line-through' : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-100'}`}
                    title="Click to show only, right-click to hide"
                  >
                    {s.label} <span className="ml-1 opacity-70">({statusCounts[s.value]||0})</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Exit Strategy + Location */}
          <div className="flex flex-wrap gap-4 items-end pt-2 border-t border-neutral-200">
            <div>
              <label className="text-xs font-medium text-neutral-600 block mb-1">Exit Strategy</label>
              <select value={filterEndUse} onChange={e=>setFilterEndUse(e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs">
                <option value="all">All</option>
                <option value="retail">Retail</option>
                <option value="wholesale">Wholesale</option>
                <option value="dealer_direct">Byrider/DD</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 block mb-1">Location</label>
              <select value={filterLocation} onChange={e=>setFilterLocation(e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs">
                <option value="all">All Locations</option>
                {allLocations.map(l=><option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <button onClick={()=>{setFilterEndUse('all');setFilterLocation('all');setShowOnlyStatuses([]);setHiddenStatuses([]);}} className="px-3 py-1.5 text-xs bg-neutral-200 rounded-lg hover:bg-neutral-300">Clear All Filters</button>
          </div>
        </div>
      )}

      <p className="text-xs text-neutral-400 italic">Drag cards between sections to change status. Click a card to open detail panel.</p>

      {activeGrp === 'all' ? (
        STATUS_GROUPS.map(g => {
          const gv = grpV(g.key);
          if (gv.length === 0) return null;
          return (
            <div key={g.key} onDragOver={e=>onDragOver(e,g.key)} onDragLeave={()=>setDragOver(null)} onDrop={e=>onDrop(e,g.key)}
              className={"rounded-xl border-2 transition-colors " + (dragOver===g.key?'border-blue-400 bg-blue-50':'border-neutral-200 bg-white')}>
              <div className="px-4 py-3 border-b bg-neutral-50 rounded-t-xl flex items-center gap-2">
                <span className="font-semibold text-sm">{g.label}</span>
                <span className="bg-neutral-200 text-neutral-700 text-xs px-2 py-0.5 rounded-full">{gv.length}</span>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{renderCards(gv)}</div>
            </div>
          );
        })
      ) : (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {renderCards(filt)}
          {filt.length===0 && <div className="col-span-3 text-center py-10 text-neutral-400">No vehicles in this category</div>}
        </div>
      )}

      {/* Detail Panel */}
      {detailVehicle && (
        <DetailPanel vehicle={detailVehicle} thumbUrl={thumbs[detailVehicle.id]}
          onClose={()=>setDetailVehicle(null)} onUpdate={upd} onRO={onNavigateToRO}
          onCreateRO={handleCreateRO} creatingRO={creatingRO} saving={!!saving}/>
      )}

      {/* Add to Run List Modal */}
      {addToListVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setAddToListVehicle(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Add to Run List</h3>
            <p className="text-sm text-neutral-500 mb-4">{addToListVehicle.stockNumber} - {addToListVehicle.year} {addToListVehicle.make} {addToListVehicle.model}</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">Select Destination</label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {SALE_DESTINATIONS.map(dest => (
                  <button key={dest.value} onClick={() => addToRunList(addToListVehicle, dest.value)}
                    className="w-full px-4 py-3 text-left border border-neutral-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition">
                    <span className="font-medium">{dest.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => setAddToListVehicle(null)} className="w-full px-4 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200">Cancel</button>
          </div>
        </div>
      )}

      {/* Run List Modal */}
      {showRunList && (() => {
        const SHOP_CATEGORIES = [
          { value: 'mech_shop', label: 'Mech Shop', color: 'bg-blue-600' },
          { value: 'outside_repairs', label: 'Outside Repairs', color: 'bg-purple-600' },
          { value: 'detail', label: 'Detail', color: 'bg-pink-600' },
          { value: 'chris', label: 'Chris', color: 'bg-orange-600' },
          { value: 'done', label: 'Done', color: 'bg-green-600' },
        ];
        const syncedList = runList.map(item => {
          const live = vehicles.find(v => v.id === item.id);
          if (!live) return item;
          const sts = live.reconWorkflowStatuses && live.reconWorkflowStatuses.length > 0
            ? live.reconWorkflowStatuses
            : live.reconWorkflowStatus ? [live.reconWorkflowStatus] : item.needs;
          return { ...item, needs: sts, location: live.currentLocation || item.location };
        });
        const groupedByCat = [
          ...SHOP_CATEGORIES.map(cat => ({ ...cat, vehicles: syncedList.filter(v => v.assignee === cat.value) })),
          { value: 'unassigned', label: 'Unassigned', color: 'bg-neutral-600', vehicles: syncedList.filter(v => !v.assignee || !SHOP_CATEGORIES.some(c => c.value === v.assignee)) }
        ].filter(g => g.vehicles.length > 0);
        return (
        <div className="fixed inset-0 bg-black flex flex-col z-50">
          <div className="flex items-center justify-between p-4 border-b border-green-500">
            <h2 className="text-xl font-bold text-white font-mono">WEEKLY RUN LIST</h2>
            <div className="flex gap-2">
              <button onClick={() => { const w = window.open('','_blank'); if(w){w.document.write('<pre style="font-family:monospace;background:#fff;color:#000;padding:20px;font-weight:bold;font-size:12px;line-height:1.6">'+groupedByCat.map(g=>`\n${'═'.repeat(50)}\n  ${g.label.toUpperCase()} (${g.vehicles.length})\n${'═'.repeat(50)}\n`+g.vehicles.map(v=>`[${v.stockNumber}] ${v.year} ${v.make} ${v.model} → ${v.destination}\nNEEDS: ${Array.isArray(v.needs)?v.needs.map(n=>sLabel(n)).join(' / '):sLabel(v.needs)}${v.runListNotes ? `\nTASK: ${v.runListNotes}` : ''}\nLOC: ${v.location}\n`).join('\n')).join('\n')+'</pre>');w.document.close();w.print();} }} disabled={runList.length===0} className="px-4 py-2 bg-green-600 text-black font-mono text-sm font-bold rounded hover:bg-green-500 disabled:opacity-50">PRINT</button>
              <button onClick={clearRunList} disabled={runList.length===0} className="px-4 py-2 bg-red-600 text-white font-mono text-sm font-bold rounded hover:bg-red-500 disabled:opacity-50">CLEAR ALL</button>
              <button onClick={() => setShowRunList(false)} className="px-4 py-2 bg-neutral-700 text-white font-mono text-sm font-bold rounded hover:bg-neutral-600">CLOSE</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-6 font-mono text-sm">
            {syncedList.length === 0 ? (
              <div className="text-center text-green-500 py-20"><div className="text-2xl mb-2">[ EMPTY ]</div><div className="text-neutral-500">No vehicles added to run list yet.</div></div>
            ) : (
              <div className="max-w-4xl mx-auto">
                <div className="text-neutral-500 mb-4">LOTSENSE DMS • {new Date().toLocaleDateString()}</div>
                {groupedByCat.map(group => (
                  <div key={group.value} className="mb-8">
                    <div className="text-green-400 text-xl font-bold mb-3 border-b border-green-500 pb-1">
                      {group.label.toUpperCase()}
                      <span className="text-neutral-500 text-sm ml-2">({group.vehicles.length})</span>
                    </div>
                    <div className="border-l-2 border-green-800 pl-4">
                      {group.vehicles.map(v => (
                        <div key={v.id} className="py-3 border-b border-neutral-800 flex justify-between items-start">
                          <div className="flex-1">
                            <div><span className="text-white font-bold">[{v.stockNumber}]</span><span className="text-green-400 ml-2">{v.year} {v.make} {v.model}</span><span className="text-neutral-500 ml-2">→ {v.destination || 'N/A'}</span></div>
                            <div className="text-yellow-400">NEEDS: {Array.isArray(v.needs) ? v.needs.map(n => sLabel(n)).join(' / ') : sLabel(v.needs)}</div>
                            {v.notes && <div className="text-neutral-500 italic text-xs">GAME PLAN: {v.notes}</div>}
                            <div className="text-neutral-500">LOC: {v.location}</div>
                            <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                              <span className="text-neutral-600 text-xs mr-1">ASSIGN:</span>
                              {SHOP_CATEGORIES.map(cat => (
                                <button key={cat.value} onClick={() => updateRunListField(v.id, 'assignee', v.assignee === cat.value ? '' : cat.value)}
                                  className={`px-2 py-0.5 rounded text-xs font-bold font-mono transition ${v.assignee === cat.value ? cat.color + ' text-white' : 'bg-neutral-800 text-neutral-400 hover:text-white'}`}>{cat.label}</button>
                              ))}
                            </div>
                            <div className="mt-1.5">
                              <input type="text" value={v.runListNotes || ''} onChange={e => updateRunListField(v.id, 'runListNotes', e.target.value)} placeholder="Task notes..."
                                className="w-full bg-neutral-900 border border-green-800 text-green-300 font-mono text-xs px-2 py-1 rounded focus:outline-none focus:border-green-400" />
                            </div>
                          </div>
                          <button onClick={() => removeFromRunList(v.id)} className="text-red-500 hover:text-red-400 text-xs ml-2">[REMOVE]</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="border-t-2 border-green-500 pt-4 mt-8">
                  <div className="text-white font-bold">SUMMARY: {syncedList.length} vehicle{syncedList.length!==1?'s':''}</div>
                  {groupedByCat.map(g => <div key={g.value} className="text-green-400">{g.label}: {g.vehicles.length}</div>)}
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Punch List Modal */}
      {selectedVendorType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedVendorType(null)}>
          <div className="bg-white w-full max-w-3xl rounded-xl shadow-xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h2 className="font-semibold text-lg">{VENDOR_TYPES.find(v => v.value === selectedVendorType)?.label || selectedVendorType}</h2>
                <p className="text-sm text-neutral-500">{loadingPunchList ? 'Loading...' : `${punchList.length} vehicle${punchList.length !== 1 ? 's' : ''}`}</p>
              </div>
              <button onClick={() => setSelectedVendorType(null)} className="text-neutral-400 hover:text-neutral-600 text-2xl px-2">×</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {loadingPunchList ? <div className="text-center py-8 text-neutral-400">Loading...</div> : punchList.length === 0 ? <div className="text-center py-8 text-neutral-400">No vehicles need this service</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50"><tr><th className="px-3 py-2 text-left">Stock #</th><th className="px-3 py-2 text-left">Vehicle</th><th className="px-3 py-2 text-left">Notes</th><th className="px-3 py-2 text-right">Actions</th></tr></thead>
                  <tbody className="divide-y">
                    {punchList.map(v => (
                      <tr key={v.id} className="hover:bg-neutral-50">
                        <td className="px-3 py-2 font-mono">{v.stockNumber}</td>
                        <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                        <td className="px-3 py-2 text-xs">{v.vendorNotes || v.complaint || '-'}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => setCostPrompt({ vehicle: v, vendorType: selectedVendorType })} className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700">✓ Complete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cost Prompt */}
      {costPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setCostPrompt(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Mark Complete & Enter Cost</h3>
            <p className="text-sm text-neutral-500 mb-4">{costPrompt.vehicle.stockNumber} - {costPrompt.vehicle.year} {costPrompt.vehicle.make} {costPrompt.vehicle.model}</p>
            <CostForm onSubmit={handleCostSubmit} onClose={() => setCostPrompt(null)} />
          </div>
        </div>
      )}

      {/* Admin Todo Modal */}
      {todoVehicle && (
        <AdminTodoModal vehicle={todoVehicle} todos={adminTodos} onAdd={addTodo} onToggle={toggleTodo} onDelete={deleteTodo} onClose={() => setTodoVehicle(null)} />
      )}
    </div>
  );
};

// Simple cost form
const CostForm = ({ onSubmit, onClose }) => {
  const [cost, setCost] = useState('');
  const [saving, setSaving] = useState(false);
  return (
    <form onSubmit={async (e) => { e.preventDefault(); setSaving(true); await onSubmit(parseFloat(cost)||0, false); setSaving(false); }}>
      <div className="mb-4">
        <label className="block text-sm font-medium text-neutral-700 mb-1">Actual Cost</label>
        <input type="number" step="0.01" value={cost} onChange={e => setCost(e.target.value)} placeholder="0.00" className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-lg" autoFocus />
      </div>
      <div className="flex gap-3">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200">Cancel</button>
        <button type="submit" disabled={saving || !cost} className="flex-1 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">{saving ? 'Saving...' : '✓ Complete'}</button>
      </div>
    </form>
  );
};

// Admin Todo Modal
const AdminTodoModal = ({ vehicle, todos, onAdd, onToggle, onDelete, onClose }) => {
  const [newText, setNewText] = useState('');
  const vehicleTodos = todos.filter(t => t.vehicleId === vehicle.id);
  const pending = vehicleTodos.filter(t => !t.done);
  const completed = vehicleTodos.filter(t => t.done);
  const handleAdd = () => { if (newText.trim()) { onAdd(vehicle.id, newText); setNewText(''); } };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center bg-neutral-50">
          <div>
            <h2 className="font-semibold text-base">Admin Tasks</h2>
            <p className="text-xs text-neutral-500">{vehicle.stockNumber} — {vehicle.year} {vehicle.make} {vehicle.model}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl px-2">×</button>
        </div>
        <div className="p-4 flex gap-2">
          <input type="text" value={newText} onChange={e => setNewText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="Add a task..." className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg" autoFocus />
          <button onClick={handleAdd} disabled={!newText.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Add</button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {pending.length === 0 && completed.length === 0 && <div className="text-center py-6 text-neutral-400 text-sm">No tasks yet.</div>}
          {pending.map(t => (
            <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 group">
              <input type="checkbox" checked={false} onChange={() => onToggle(t.id)} className="rounded text-blue-600 flex-shrink-0" />
              <span className="flex-1 text-sm text-neutral-800">{t.text}</span>
              <button onClick={() => onDelete(t.id)} className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm">×</button>
            </div>
          ))}
          {completed.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] text-neutral-400 uppercase tracking-wider font-semibold mb-1">Completed</div>
              {completed.map(t => (
                <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 group">
                  <input type="checkbox" checked={true} onChange={() => onToggle(t.id)} className="rounded text-green-600 flex-shrink-0" />
                  <span className="flex-1 text-sm text-neutral-400 line-through">{t.text}</span>
                  <button onClick={() => onDelete(t.id)} className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopWorkflow;
